/* AgriBasket app commerce controller.
   Progressive enhancement only: every product form still posts to Shopify natively,
   and this file merely intercepts those same forms to add the line through the
   Cart AJAX API so the shopper never loses their place on the listing page. */
(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  const CART_ADD = '/cart/add.js';
  const CART_URL = '/cart';
  const WA_BASE = 'https://wa.me/918669791655';

  /* ---------------- translations ----------------
     Every string below comes from the theme locale files through
     snippets/agribasket-js-i18n.liquid, so switching language changes the listing
     cards, the cart count and the whole add-to-cart flow. */
  const I18N = (window.AgroBucketI18n && window.AgroBucketI18n.strings) || {};
  const tr = (key, fallback, vars) => {
    let out = typeof I18N[key] === 'string' && I18N[key] ? I18N[key] : fallback;
    if (out == null) out = '';
    if (vars) {
      out = String(out).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match);
    }
    return out;
  };

  /* ---------- small helpers ---------- */
  const parseJson = (value) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const sectionIdsFor = (scope) => {
    const ids = [];
    if (scope.dataset && scope.dataset.abCartSections) {
      String(scope.dataset.abCartSections)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .forEach((value) => ids.push(value));
    }
    let node = scope.parentElement;
    while (node) {
      if (node.id) ids.push(node.id);
      node = node.parentElement;
    }
    return ids;
  };

  const moneyFormats = parseJson(doc.body && doc.body.dataset ? doc.body.dataset.abMoney : null);

  const formatMoney = (cents) => {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';
    const pattern = moneyFormats && moneyFormats.money_format ? moneyFormats.money_format : '\u20b9{{amount}}';
    const decimals = Number(moneyFormats && moneyFormats.decimals);
    const fixed = Number.isFinite(decimals) ? amount / 100 : amount / 100;
    const rendered = Number.isFinite(decimals) ? fixed.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '') : fixed.toFixed(2);
    if (pattern.indexOf('{{amount_no_decimals}}') > -1) {
      return pattern.replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(amount / 100)));
    }
    if (pattern.indexOf('{{amount}}') > -1) {
      return pattern.replace(/\{\{\s*amount\s*\}\}/, rendered);
    }
    return '\u20b9' + rendered;
  };

  /* ---------- header cart count ---------- */
  const headerBadge = () => {
    const host = doc.querySelector('.ab-cart');
    if (!host) return null;
    let badge = host.querySelector(':scope > i');
    if (!badge) {
      badge = doc.createElement('i');
      badge.setAttribute('aria-hidden', 'true');
      host.appendChild(badge);
    }
    return badge;
  };

  const paintHeaderCount = (count) => {
    const host = doc.querySelector('.ab-cart');
    const badge = headerBadge();
    if (!host || !badge) return;
    const total = Number(count) || 0;
    badge.textContent = total > 0 ? String(total) : '';
    host.setAttribute('aria-label', total > 0
      ? tr('nav.cart_count', 'Cart, {{ count }} items', { count: total })
      : tr('nav.cart', 'Cart'));
    if (total > 0) host.setAttribute('data-ab-cart-filled', '');
    else host.removeAttribute('data-ab-cart-filled');
  };

  const refreshHeaderCount = async () => {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) return;
      const cart = await response.json();
      paintHeaderCount(cart.item_count);
    } catch (error) {
      /* The badge simply keeps its server-rendered value. */
    }
  };

  /* ---------- cart add ---------- */
  const addToCart = async (id, quantity) => {
    const response = await fetch(CART_ADD, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(id), quantity: Number(quantity) || 1 }] })
    });
    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }
    if (!response.ok) {
      const message = payload && (payload.description || payload.message);
      throw new Error(message || tr('product.add_failed', 'This item could not be added to your cart.'));
    }
    return payload;
  };

  const cartCountAfter = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return null;
      const cart = await response.json();
      return cart.item_count;
    } catch (error) {
      return null;
    }
  };

  /* ---------- contact sheet contract: [data-ab-contact-open] + data-ab-contact-context ---------- */
  const CONTACT_SELECTOR = '[data-ab-contact-open]';
  const openContact = (context, source) => {
    const trigger = doc.querySelector(CONTACT_SELECTOR);
    if (!trigger) return false;
    if (context) trigger.setAttribute('data-ab-contact-context', context);
    else trigger.removeAttribute('data-ab-contact-context');
    trigger.click();
    if (source && typeof source.focus === 'function') source.focus();
    return true;
  };

  const bulkContext = (data, variantTitle) => {
    const parts = [tr('contact.bulk', 'Contact for Bulk Order') + ': ' + data.abTitle];
    if (variantTitle && variantTitle !== 'Default Title') parts.push(variantTitle);
    parts.push(window.location.origin + data.abUrl);
    return parts.join('\n');
  };

  const whatsappBulk = (data, variantTitle) => {
    const text = bulkContext(data, variantTitle);
    window.open(WA_BASE + '?text=' + encodeURIComponent(text), '_blank', 'noopener');
  };

  /* ---------- inline confirmation ---------- */
  const feedbackFor = (scope) => scope.querySelector('[data-ab-buy-feedback]');

  const clearFeedback = (scope) => {
    const box = feedbackFor(scope);
    if (!box) return;
    box.hidden = true;
    box.textContent = '';
    box.classList.remove('is-error');
  };

  const showFeedback = (scope, options) => {
    const box = feedbackFor(scope);
    if (!box) return;
    box.textContent = '';
    box.classList.toggle('is-error', options && options.error === true);
    box.hidden = false;

    const title = doc.createElement('span');
    title.className = 'ab-buy-feedback__title';
    title.textContent = options.title;
    box.appendChild(title);

    if (options.detail) {
      const detail = doc.createElement('span');
      detail.textContent = ' ' + options.detail;
      box.appendChild(detail);
    }

    if (options.goToCart) {
      const link = doc.createElement('a');
      link.className = 'ab-buy-feedback__go';
      link.href = CART_URL;
      link.textContent = tr('product.go_to_cart', 'Go to Cart');
      box.appendChild(doc.createTextNode(' '));
      box.appendChild(link);
    }

    if (options.bulk) {
      const bulk = doc.createElement('button');
      bulk.type = 'button';
      bulk.className = 'ab-buy-feedback__bulk';
      bulk.setAttribute('data-ab-bulk-order', '');
      bulk.textContent = tr('contact.bulk', 'Contact for Bulk Order');
      box.appendChild(doc.createTextNode(' '));
      box.appendChild(bulk);
    }
  };

  /* ---------- single-variant direct add ---------- */
  const wireDirectForm = (form) => {
    if (form.dataset.abCommerceReady) return;
    form.dataset.abCommerceReady = '1';

    const button = form.querySelector('[data-ab-buy]');
    if (!button) return;

    const directWrap = form.closest('[data-ab-direct]') || form;
    if (!directWrap.dataset.abCartSections) directWrap.dataset.abCartSections = sectionIdsFor(form).join(',');

    form.addEventListener('submit', async (event) => {
      if (form.dataset.abNoEnhance === '1') return;
      if (!window.fetch || !window.Promise) return;

      const id = form.querySelector('[name="id"]');
      const quantity = form.querySelector('[name="quantity"]');
      if (!id || !id.value) return;

      event.preventDefault();
      if (button.getAttribute('aria-disabled') === 'true') return;

      clearFeedback(directWrap);
      button.classList.add('is-busy');
      button.setAttribute('aria-disabled', 'true');
      const original = button.textContent;
      button.textContent = tr('product.adding', 'Adding\u2026');

      try {
        await addToCart(id.value, quantity ? quantity.value : 1);
        const count = await cartCountAfter();
        if (Number.isFinite(count)) paintHeaderCount(count);
        else await refreshHeaderCount();
        button.textContent = tr('product.added', 'Added');
        showFeedback(directWrap, {
          title: button.dataset.abAddedTitle || tr('product.added_to_cart', 'Added to cart.'),
          detail: button.dataset.abTitle ? button.dataset.abTitle : '',
          goToCart: true,
          bulk: true
        });
        window.setTimeout(() => {
          button.textContent = original;
          button.removeAttribute('aria-disabled');
        }, 2600);
      } catch (error) {
        button.textContent = original;
        button.removeAttribute('aria-disabled');
        showFeedback(directWrap, {
          title: tr('product.add_failed_title', 'We could not add this automatically.'),
          detail: error && error.message ? error.message : tr('product.add_failed_refresh', 'Refresh the page and try again.'),
          error: true
        });
      } finally {
        button.classList.remove('is-busy');
      }
    });
  };

  /* ---------- variant sheet ---------- */
  let sheetOpen = false;
  let lastTrigger = null;
  let activeSheet = null;
  const initiallyInert = new Map();

  const focusableIn = (container) => Array.prototype.filter.call(
    container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ),
    (node) => !node.hasAttribute('hidden') && node.offsetParent !== null
  );

  /* ---------- scroll lock (shared ref-count contract) ----------
     app-header.js keeps a reference count for its own sheets and only drops the
     `ab-lock` class when that count reaches zero. This controller joins that same
     contract: one page-level counter lives on window, so a sheet closing here can
     never unlock a sheet that is still open elsewhere, and the inline overflow
     guard keeps the page locked if another owner drops the class while we hold one. */
  const LOCK_KEY = '__agribasketScrollLock';
  const lockRegistry = () => {
    const existing = window[LOCK_KEY];
    if (existing && typeof existing.acquire === 'function' && typeof existing.release === 'function') return existing;

    const state = { count: 0, foreignAtStart: false };
    const api = {
      count: () => state.count,
      acquire: () => {
        state.count += 1;
        if (state.count === 1) {
          state.foreignAtStart = root.classList.contains('ab-lock');
          root.style.overflow = 'hidden';
        }
        root.classList.add('ab-lock');
        return state.count;
      },
      release: () => {
        state.count = Math.max(0, state.count - 1);
        if (state.count > 0) return state.count;
        root.style.overflow = '';
        const stacked = doc.querySelector('dialog[open]');
        if (!state.foreignAtStart && !stacked) root.classList.remove('ab-lock');
        state.foreignAtStart = false;
        return state.count;
      }
    };

    window[LOCK_KEY] = api;
    return api;
  };

  const closeSheet = () => {
    if (!activeSheet) return;
    const sheet = activeSheet;
    activeSheet = null;
    sheetOpen = false;
    sheet.release();
  };

  const onSheetKeydown = (event) => {
    if (!activeSheet) return;
    const sheet = activeSheet;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeSheet();
      return;
    }

    if (event.key !== 'Tab') return;

    const items = focusableIn(sheet.dialog);
    if (!items.length) {
      event.preventDefault();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    const current = doc.activeElement;

    if (!sheet.dialog.contains(current)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const sheetFallback = (dialog) => {
    const wrap = doc.createElement('div');
    wrap.className = 'ab-vsheet__wrap';
    wrap.style.cssText = 'align-items:flex-end;background:#002d1da6;display:flex;inset:0;justify-content:center;position:fixed;z-index:80';
    dialog.removeAttribute('hidden');
    dialog.setAttribute('open', '');
    dialog.parentNode.insertBefore(wrap, dialog);
    wrap.appendChild(dialog);

    wrap.addEventListener('click', (event) => {
      if (event.target === wrap) closeSheet();
    });

    doc.querySelectorAll('body > *').forEach((node) => {
      if (node === wrap) return;
      initiallyInert.set(node, node.hasAttribute('inert'));
      node.setAttribute('inert', '');
    });

    return wrap;
  };

  const wireSheetForm = (sheet, form) => {
    if (form.dataset.abCommerceReady) return;
    form.dataset.abCommerceReady = '1';

    /* One real submit button per available variant. Each button carries its own
       variant id, so a scripting shopper is upgraded to the AJAX call while the
       same form still posts that exact variant with scripting switched off. */
    const buttons = Array.from(form.querySelectorAll('[data-ab-vsheet-add]'));
    if (!buttons.length) return;

    const setBusy = (busy) => {
      buttons.forEach((node) => {
        if (busy) {
          node.classList.add('is-busy');
          node.setAttribute('aria-disabled', 'true');
        } else {
          node.classList.remove('is-busy');
          node.removeAttribute('aria-disabled');
        }
      });
    };

    form.addEventListener('submit', async (event) => {
      if (form.dataset.abSkipIntercept === '1') {
        form.dataset.abSkipIntercept = '';
        return;
      }
      if (!window.fetch || !window.Promise) return;

      const fallback = buttons.filter((node) => !node.disabled)[0];
      const submitter = event.submitter && event.submitter.matches('[data-ab-vsheet-add]')
        ? event.submitter
        : fallback;
      if (!submitter || !submitter.value) return;

      const quantity = form.querySelector('[name="quantity"]');

      event.preventDefault();
      const scope = form.closest('[data-ab-vsheet-shell]') || form;
      clearFeedback(scope);
      const label = submitter.textContent;
      setBusy(true);
      submitter.textContent = tr('product.adding', 'Adding\u2026');

      try {
        await addToCart(submitter.value, quantity ? quantity.value : 1);
        const count = await cartCountAfter();
        if (Number.isFinite(count)) paintHeaderCount(count);
        else await refreshHeaderCount();

        /* The confirmation belongs to the card whose button opened the sheet, so the
           trigger is read back before closeSheet() clears lastTrigger. Cards that have
           no opener of their own fall back to the first result host on the page. */
        const opener = lastTrigger;
        const variant = submitter.getAttribute('data-ab-variant') || '';
        closeSheet();
        const host = (opener && typeof opener.closest === 'function'
          ? opener.closest('[data-ab-direct][data-ab-vsheet-result]')
          : null) || doc.querySelector('[data-ab-direct][data-ab-vsheet-result]') || null;
        if (host) {
          const product = host.getAttribute('data-ab-title') || '';
          const detail = variant && variant !== 'Default Title'
            ? (product ? product + ' \u00b7 ' + variant : variant)
            : product;
          showFeedback(host, {
            title: tr('product.added_to_cart', 'Added to cart.'),
            detail: detail,
            goToCart: true,
            bulk: true
          });
          host.scrollIntoView({ block: 'nearest' });
        }
      } catch (error) {
        showFeedback(scope, {
          title: tr('product.add_failed_title', 'We could not add this automatically.'),
          detail: error && error.message ? error.message : tr('product.add_failed_refresh', 'Refresh the page and try again.'),
          error: true
        });
      } finally {
        submitter.textContent = label;
        setBusy(false);
      }
    });
  };

  const openSheet = (dialog, trigger) => {
    if (!dialog || sheetOpen) return;
    sheetOpen = true;
    lastTrigger = trigger || doc.activeElement;

    const locks = lockRegistry();
    const modal = typeof dialog.showModal === 'function';
    let released = false;
    const sheet = {
      dialog,
      modal,
      release: () => {
        if (released) return;
        released = true;
        doc.removeEventListener('keydown', onSheetKeydown, true);
        dialog.removeAttribute('aria-modal');

        if (modal) {
          try {
            dialog.close();
          } catch (error) {
            dialog.removeAttribute('open');
          }
        } else {
          dialog.removeAttribute('open');
          dialog.setAttribute('hidden', 'hidden');
          initiallyInert.forEach((value, node) => {
            if (value) node.setAttribute('inert', '');
            else node.removeAttribute('inert');
          });
          initiallyInert.clear();
          if (sheet.__wrap) {
            sheet.__wrap.style.display = 'none';
            if (sheet.__createdWrap && sheet.__wrap.parentNode) {
              if (dialog.parentNode === sheet.__wrap) sheet.__wrap.parentNode.insertBefore(dialog, sheet.__wrap);
              sheet.__wrap.parentNode.removeChild(sheet.__wrap);
            }
          }
        }

        locks.release();

        if (lastTrigger && doc.contains(lastTrigger)) lastTrigger.focus();
        lastTrigger = null;
      }
    };

    if (modal) {
      /* A dialog that still carries the hidden attribute is display:none, so it
         stays invisible in the top layer until the attribute is cleared. */
      dialog.removeAttribute('hidden');
      try {
        dialog.showModal();
      } catch (error) {
        dialog.setAttribute('open', '');
      }
    } else {
      sheet.__wrap = sheetFallback(dialog);
      sheet.__createdWrap = true;
    }

    activeSheet = sheet;
    locks.acquire();
    /* aria-modal is set only while the dialog is actually modal: both the native
       showModal() path and the manual overlay path inert the rest of the page. */
    dialog.setAttribute('aria-modal', 'true');
    doc.addEventListener('keydown', onSheetKeydown, true);

    if (!dialog.dataset.abSheetChrome) {
      dialog.dataset.abSheetChrome = '1';
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeSheet();
      });
      dialog.addEventListener('close', () => {
        if (activeSheet && activeSheet.dialog === dialog) closeSheet();
      });
    }

    const form = dialog.querySelector('form[data-ab-vsheet-form]');
    if (form) wireSheetForm(sheet, form);

    window.requestAnimationFrame(() => {
      const close = dialog.querySelector('[data-ab-vsheet-close]');
      const firstAdd = dialog.querySelector('[data-ab-vsheet-add]:not([disabled])');
      const target = close || firstAdd;
      if (target && typeof target.focus === 'function') target.focus();
    });
  };

  /* ---------- delegated wiring ---------- */
  const initScope = (scope) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;
    scope.querySelectorAll('form[data-ab-commerce-form]').forEach((form) => {
      if (form.hasAttribute('data-ab-vsheet-form')) return;
      wireDirectForm(form);
    });
    syncWishlist(scope);
  };

  doc.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target.closest('form[data-ab-commerce-form]') : null;
    if (!form || form.hasAttribute('data-ab-vsheet-form')) return;
    wireDirectForm(form);
  }, true);

  doc.addEventListener('click', (event) => {
    const node = event.target instanceof Element ? event.target : null;
    if (!node) return;

    const heart = node.closest('[data-wishlist], [data-listing-wishlist]');
    if (heart) {
      event.preventDefault();
      toggleWishlist(heart);
      return;
    }

    const opener = node.closest('[data-ab-vsheet-open]');
    if (opener) {
      event.preventDefault();
      const dialog = doc.querySelector('[data-ab-vsheet="' + opener.dataset.abVsheetOpen + '"]');
      openSheet(dialog, opener);
      return;
    }

    if (node.closest('[data-ab-vsheet-close]')) {
      event.preventDefault();
      closeSheet();
      return;
    }

    const bulk = node.closest('[data-ab-bulk-order]');
    if (bulk) {
      event.preventDefault();
      const scope = bulk.closest('[data-ab-direct]');
      if (!scope) return;
      const context = bulkContext(scope.dataset, '');
      if (!openContact(context, bulk)) whatsappBulk(scope.dataset, '');
    }
  });

  /* ---------- card wishlist hearts ----------
     These live on the product rail and listing cards. The behavior used to sit in
     inline <script> blocks, which CSP blocks; it is centralised here instead.

     State is one localStorage list shared with the PDP and the wishlist page. Every
     tap re-reads that list before writing, so saving two different products in a row
     cannot drop the first one, and every heart on the page repaints from that single
     source of truth. Clicks are delegated, so hearts keep working inside product
     cards that Shopify re-renders or another script injects later. */
  const WISHLIST_KEY = 'agribasket-wishlist';
  const WISHLIST_EVENT = 'agribasket:wishlist-change';
  const WISHLIST_BOUND = 'abWishlistBound';

  const readWishlist = () => {
    const value = parseJson(window.localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()) : [];
  };
  const writeWishlist = (value) => {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(value));
    } catch (error) {
      /* Storage can be unavailable in private modes; the button still toggles. */
    }
  };
  const wishlistHandleOf = (button) =>
    button.getAttribute('data-wishlist') || button.getAttribute('data-listing-wishlist') || '';
  const wishlistButtonsIn = (scope) =>
    Array.prototype.slice.call((scope || doc).querySelectorAll('[data-wishlist], [data-listing-wishlist]'));

  /* Repaint every heart in scope from what is actually stored. */
  const syncWishlist = (scope) => {
    const buttons = wishlistButtonsIn(scope);
    if (!buttons.length) return;
    const saved = readWishlist();
    buttons.forEach((button) => {
      const handle = wishlistHandleOf(button);
      if (!handle) return;
      const active = saved.indexOf(handle) > -1;
      button.textContent = active ? '\u2665' : '\u2661';
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.classList.toggle('is-saved', active);
      const title = button.getAttribute('data-ab-wishlist-title');
      if (title) {
      button.setAttribute('aria-label', active
        ? tr('product.remove_wishlist_label', 'Remove {{ title }} from wishlist', { title: title })
        : tr('product.save_wishlist_label', 'Save {{ title }} to wishlist', { title: title }));
      }
    });
  };

  const toggleWishlist = (button) => {
    const handle = wishlistHandleOf(button);
    if (!handle) return;
    const saved = readWishlist();
    const index = saved.indexOf(handle);
    if (index < 0) saved.push(handle);
    else saved.splice(index, 1);
    writeWishlist(saved);
    syncWishlist(doc);
    doc.dispatchEvent(new CustomEvent(WISHLIST_EVENT, {
      detail: { handle: handle, saved: saved.slice(), active: index < 0 }
    }));
  };

  /* Bound once per document: a page that renders several rails loads this file more
     than once, and a doubled listener would toggle a heart twice per tap. */
  if (!root.dataset[WISHLIST_BOUND]) {
    root.dataset[WISHLIST_BOUND] = '1';
    doc.addEventListener(WISHLIST_EVENT, () => syncWishlist(doc));
    window.addEventListener('storage', (event) => {
      if (event.key === WISHLIST_KEY) syncWishlist(doc);
    });
    /* Shopify re-renders sections (theme editor, filtered results) and other scripts
       inject cards; repaint hearts as soon as any arrive. */
    if (doc.body && typeof MutationObserver === 'function') {
      let queued = false;
      const observer = new MutationObserver((records) => {
        if (queued) return;
        const arrived = records.some((record) => Array.prototype.some.call(record.addedNodes, (node) =>
          node.nodeType === 1 && (node.hasAttribute('data-wishlist') || node.hasAttribute('data-listing-wishlist') ||
            node.querySelector('[data-wishlist], [data-listing-wishlist]'))));
        if (!arrived) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          syncWishlist(doc);
        });
      });
      observer.observe(doc.body, { childList: true, subtree: true });
    }
  }

  const boot = () => {
    initScope(doc);
    const initial = doc.querySelector('.ab-cart');
    if (initial && !initial.hasAttribute('data-ab-cart-count')) {
      const rendered = initial.querySelector(':scope > i');
      if (!rendered) initial.removeAttribute('data-ab-cart-filled');
    }
  };

  /* The sort control keeps a native GET form; this replaces the old inline
     onchange handler so no CSP-unsafe attribute is required. */
  doc.addEventListener('change', (event) => {
    const select = event.target instanceof Element ? event.target.closest('[data-ab-sort]') : null;
    if (!select || !select.form) return;
    select.form.submit();
  });

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  doc.addEventListener('shopify:section:load', (event) => initScope(event.target));
  doc.addEventListener('shopify:cart:updated', (event) => {
    const detail = event && event.detail ? event.detail : null;
    if (detail && Number.isFinite(Number(detail.item_count))) paintHeaderCount(detail.item_count);
  });
})();
